import React, { useState } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';
import FeatherIcon from 'feather-icons-react';

export interface SendAccEmailModalProps {
  isOpen: boolean;
  patientName: string;
  accClaimNumber: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * SendAccEmailModal - AXIS076 AE Validation Workflow
 * 
 * Modal for sending template email to ACC requesting employer information.
 * Staff copy the email, send it manually from their email client, then confirm.
 * 
 * - Confirm → Referral enters AE Validation queue
 */
export const SendAccEmailModal: React.FC<SendAccEmailModalProps> = ({
  isOpen,
  patientName,
  accClaimNumber,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const [copiedFeedback, setCopiedFeedback] = useState<string | null>(null);

  const templateSubject = `ACC Accredited Employer Information Required - ${patientName} (ACC# ${accClaimNumber})`;
  const templateBody = `Kia ora,

We are processing a referral for ${patientName} under an ACC Accredited Employer claim (ACC# ${accClaimNumber}).

To complete our records and proceed with billing, we require the following information:

- Accredited Employer Name

Please provide these details at your earliest convenience.

Thank you,
[Your Name]
[Clinic Name]
Phone: [Reception Phone]
Email: [Reception Email]`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      isLoading={isLoading}
      style={{ maxWidth: '650px' }}
    >
      <div className="modal-header">
        <h3>Send Template Email to ACC</h3>
      </div>
      <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <p style={{ marginBottom: '4px' }}>
          <strong>Patient:</strong> {patientName}
        </p>
        <p style={{ marginBottom: '12px' }}>
          <strong>ACC Claim Number:</strong> {accClaimNumber}
        </p>
        
        <div style={{ marginBottom: '12px' }}>
          <p style={{ marginBottom: '8px' }}>
            Copy the template email below and send it to ACC from your email client to request the Accredited Employer name.
          </p>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '0' }}>
            After sending the email, click Confirm to move this referral to the AE Validation queue.
          </p>
        </div>

        {/* Template Email Section */}
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '16px', 
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>Template Email to ACC</strong>
          
          {/* Subject Section */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '6px'
            }}>
              <strong style={{ fontSize: '13px' }}>Subject</strong>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(templateSubject);
                  setCopiedFeedback('✓ Subject copied to clipboard');
                  setTimeout(() => setCopiedFeedback(''), 2000);
                }}
                disabled={isLoading}
              >
                <FeatherIcon icon="copy" size={14} style={{ marginRight: '4px' }} />
                Copy Subject
              </Button>
            </div>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '12px', 
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '13px',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap'
            }}>
              {templateSubject}
            </div>
          </div>

          {/* Body Section */}
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '6px'
            }}>
              <strong style={{ fontSize: '13px' }}>Body</strong>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(templateBody);
                  setCopiedFeedback('✓ Body copied to clipboard');
                  setTimeout(() => setCopiedFeedback(''), 2000);
                }}
                disabled={isLoading}
              >
                <FeatherIcon icon="copy" size={14} style={{ marginRight: '4px' }} />
                Copy Body
              </Button>
            </div>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '12px', 
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '13px',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {templateBody}
            </div>
          </div>
          
          {copiedFeedback && (
            <p style={{ 
              color: copiedFeedback.startsWith('✓') ? '#4caf50' : '#f44336',
              fontSize: '13px',
              marginTop: '8px',
              marginBottom: '0'
            }}>
              {copiedFeedback}
            </p>
          )}
        </div>

        <div style={{ 
          backgroundColor: '#fff3cd', 
          padding: '12px', 
          borderRadius: '4px',
          border: '1px solid #ffc107',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          
        }}>
          <FeatherIcon icon="info" size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          <strong>Important:</strong> Make sure to send the email before clicking Confirm.
        </div>
      </div>
      
      <div className="modal-footer">
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={isLoading}
          isLoading={isLoading}
        >
          Confirm - Email Sent
        </Button>
      </div>
    </Modal>
  );
};
